import type { GphCompletionAct, GphServiceItem, CalculationsSummary, ServiceItemSummary } from "../types";

const APPROVED_STATUS = "approved";

const PENDING_STATUSES = new Set([
  "draft",
  "signing_pending",
  "pending_signing",
  "signed",
  "pending_review",
]);

export function clampProgress(pct: number): number {
  return Math.max(0, Math.min(100, pct));
}

export function calculateContractSummary(
  contractAmount: string,
  acts: GphCompletionAct[],
): CalculationsSummary {
  const parsedContractAmount = parseFloat(contractAmount) || 0;

  let totalPaid = 0;
  let totalApproved = 0;
  let totalPending = 0;

  for (const act of acts) {
    const amount = parseFloat(act.amount) || 0;
    if (act.is_paid) {
      totalPaid += amount;
    } else if (act.status === APPROVED_STATUS || PENDING_STATUSES.has(act.status)) {
      totalApproved += amount;
    }
  }

  const remaining = parsedContractAmount - totalPaid - totalApproved;
  const paidPercent =
    parsedContractAmount > 0
      ? clampProgress((totalPaid / parsedContractAmount) * 100)
      : 0;
  const approvedPercent =
    parsedContractAmount > 0
      ? clampProgress((totalApproved / parsedContractAmount) * 100)
      : 0;

  return {
    contractAmount: parsedContractAmount,
    totalPaid,
    totalApproved,
    totalPending,
    remaining,
    paidPercent,
    approvedPercent,
  };
}

export function calculateServiceItemSummaries(
  serviceItems: GphServiceItem[],
  completionActs: GphCompletionAct[],
  sharedPrice: boolean,
): ServiceItemSummary[] {
  const paidByService = new Map<number, number>();
  const approvedByService = new Map<number, number>();

  for (const act of completionActs) {
    if (act.is_paid) {
      for (const line of act.lines) {
        const prev = paidByService.get(line.service_item.id) ?? 0;
        paidByService.set(line.service_item.id, prev + (parseFloat(line.line_amount ?? "0") || 0));
      }
    } else if (act.status === APPROVED_STATUS) {
      for (const line of act.lines) {
        const prev = approvedByService.get(line.service_item.id) ?? 0;
        approvedByService.set(line.service_item.id, prev + (parseFloat(line.line_amount ?? "0") || 0));
      }
    }
  }

  return serviceItems.map(function mapServiceItem(item) {
    const hasPrice = !sharedPrice && item.price !== null;
    const total = hasPrice ? (parseFloat(item.price!) || 0) : 0;
    const paid = paidByService.get(item.id) ?? 0;
    const approved = approvedByService.get(item.id) ?? 0;
    const paidPercent = hasPrice && total > 0 ? clampProgress((paid / total) * 100) : 0;
    const approvedPercent = hasPrice && total > 0 ? clampProgress((approved / total) * 100) : 0;

    return {
      id: item.id,
      serviceName: item.service_name,
      startDate: item.start_date,
      endDate: item.end_date,
      total,
      paid,
      approved,
      paidPercent,
      approvedPercent,
      hasPrice,
    };
  });
}
